import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { removeToken, saveRefreshToken, saveUserToken } from "./authUtil";
import { PATH } from "@/constants/path";

interface TokenInterface {
  data: {
    token: string;
    refresh_token: string;
  };
}

axios.defaults.headers.post["Content-Type"] = "application/json";

const onRequest = (config: AxiosRequestConfig): AxiosRequestConfig => {
  return config;
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  return Promise.reject(error);
};

const setupInterceptorsTo = (axiosInstance: AxiosInstance): AxiosInstance => {
  axiosInstance.interceptors.request.use(onRequest, onRequestError);
  axiosInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      if (
        error.config &&
        error.response.status === 400 && // Use the status code your server returns when token has expired
        !error.config._retry
      ) {
        console.log("Access token 401");
        error.config._retry = true;
        try {
          const refreshTokenResult = await refreshToken(error.config);
          const { token } = refreshTokenResult.data.data;
          error.config.headers.Authorization = `Bearer ${token}`;
          return instance().request(error.config); // Repeat the initial request
        } catch (error) {
          console.log("Refresh Token error => to login page");
          removeToken();
          window.location.href = PATH.LOGIN;
          return Promise.reject(error);
        }
      } else {
        return Promise.reject(error);
      }
    },
  );
  return axiosInstance;
};

const instance = (auth: boolean = true) => {
  const localStorageToken = window.localStorage.getItem("token");
  return axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    timeout: 10 * 60 * 1000,
    withCredentials: false,
    headers: {
      "Content-type": "application/json",
      Authorization:
        auth && localStorageToken ? `Bearer ${localStorageToken}` : "",
    },
  });
};

export const refreshToken = (config: any) => {
  return new Promise<AxiosResponse<TokenInterface, any>>((resolve, reject) => {
    const body = JSON.stringify({
      token: localStorage.getItem("refresh_token") ?? null,
    });

    console.log("Call Refresh Token");
    instance()
      .post<TokenInterface>(
        `${process.env.REACT_APP_API_AUTHEN}/authen/refresh-tokens`,
        body,
      )
      .then((res) => {
        saveUserToken(res.data.data.token);
        saveRefreshToken(res.data.data.refresh_token);
        return resolve(res);
      })
      .catch((err) => {
        return reject(err);
      });
  });
};

const clientInstance = (auth: boolean = true) => {
  const localStorageToken = window.localStorage.getItem("token");
  return axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    timeout: 10 * 60 * 1000,
    withCredentials: false,
    headers: {
      "accept-language": "en",
      Authorization:
        auth && localStorageToken ? `Bearer ${localStorageToken}` : "",
    },
  });
};

const client = (auth: boolean = true) =>
  setupInterceptorsTo(clientInstance(auth));

export default client;

const clientAuth = (auth: boolean = false) => {
  const localStorageToken = window.localStorage.getItem("token");
  return axios.create({
    baseURL: process.env.REACT_APP_API_AUTHEN,
    timeout: 10 * 60 * 1000,
    withCredentials: false,
    headers: {
      "accept-language": "en",
      Authorization: localStorageToken ? `Bearer ${localStorageToken}` : "",
    },
  });
};

const clientAdminInstance = (auth: boolean = true) => {
  const localStorageToken = window.localStorage.getItem("token");
  return axios.create({
    baseURL: process.env.REACT_APP_API_ADMIN,
    timeout: 10 * 60 * 1000,
    withCredentials: false,
    headers: {
      "accept-language": "en",
      Authorization:
        auth && localStorageToken ? `Bearer ${localStorageToken}` : "",
    },
  });
};

const clientAdmin = (auth: boolean = true) =>
  setupInterceptorsTo(clientAdminInstance(auth));

/**
 * The ooca gateway, which is NOT where the rest of this product reads from.
 *
 * `getFetcher` points at REACT_APP_API_URL (analytic-api), which serves this product's
 * own reporting. The org-facing Pulse and onboarding endpoints are published through
 * KrakenD instead and live under /v3, so a call made with `getFetcher` reaches
 * analytic-api and 404s. Anything under /v3 belongs on this client.
 *
 * Falls back to REACT_APP_API_AUTHEN because that variable already holds the gateway
 * origin today; the dedicated name exists so the two stop being the same thing by
 * accident the first time authentication moves.
 */
/**
 * Where the gateway lives, or a loud failure.
 *
 * `??` was wrong here and cost a production outage. A workflow that passes
 * `${{ vars.REACT_APP_API_GATEWAY }}` for a variable nobody created substitutes an
 * EMPTY STRING, not undefined, and `"" ?? fallback` is `""`. axios then treats an empty
 * baseURL as "relative", so every call went to the app's own origin, where Firebase
 * rewrites any unmatched path to /index.html and answers 200 with HTML. axios saw a
 * success, the page read `.data` off a string, got undefined, and white-screened.
 *
 * So: `||`, which rejects the empty string too. And if neither is usable, throw rather
 * than fall back to a relative URL — a request that cannot reach the gateway must fail
 * where it is made, not three layers later as a type error in a component.
 */
export const gatewayBaseUrl = (): string => {
  const url =
    process.env.REACT_APP_API_GATEWAY || process.env.REACT_APP_API_AUTHEN;

  if (!url) {
    throw new Error(
      "REACT_APP_API_GATEWAY is not configured; /v3 requests have nowhere to go",
    );
  }

  return url;
};

const clientGatewayInstance = (auth: boolean = true) => {
  const localStorageToken = window.localStorage.getItem("token");
  return axios.create({
    baseURL: gatewayBaseUrl(),
    timeout: 10 * 60 * 1000,
    withCredentials: false,
    headers: {
      "Content-type": "application/json",
      Authorization:
        auth && localStorageToken ? `Bearer ${localStorageToken}` : "",
    },
  });
};

const clientGateway = (auth: boolean = true) =>
  setupInterceptorsTo(clientGatewayInstance(auth));

const getFetcher = (url: string) =>
  client(true)
    .get(url)
    .then((res) => res.data);

/** Same contract as `getFetcher`, but against the gateway. Use for every /v3 path. */
const getGatewayFetcher = (url: string) =>
  clientGateway(true)
    .get(url)
    .then((res) => res.data);

export {
  clientAdmin,
  clientAuth,
  clientGateway,
  getFetcher,
  getGatewayFetcher,
};
